import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Chevron = () => (
  <svg className="sp-dd__chev" viewBox="0 0 12 12" aria-hidden="true">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function usePortaledMenuStyle(open, anchorRef, menuWidth, menuMaxHeight = 320) {
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const pad = 8;
      const maxH = Math.min(menuMaxHeight, window.innerHeight - pad * 2);
      const spaceBelow = window.innerHeight - rect.bottom - pad;
      const spaceAbove = rect.top - pad;
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
      const top = openUp
        ? Math.max(pad, rect.top - Math.min(maxH, spaceAbove) - 6)
        : rect.bottom + 6;
      let left = rect.left + rect.width / 2;
      const menuW = menuWidth;
      left = Math.min(
        Math.max(left, pad + menuW / 2),
        window.innerWidth - pad - menuW / 2
      );
      setMenuStyle({
        position: "fixed",
        top,
        left,
        transform: "translateX(-50%)",
        maxHeight: openUp
          ? Math.min(maxH, spaceAbove)
          : Math.min(maxH, Math.max(160, spaceBelow)),
        overflowY: "auto",
        overflowX: "hidden",
        zIndex: 10050,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, menuWidth, menuMaxHeight, anchorRef]);

  return menuStyle;
}

/**
 * Compact cute dropdown — menu portaled so parents can’t clip it.
 * Shared by Signature + Field toolbars.
 */
export function CuteDropdown({
  id,
  openId,
  setOpenId,
  label,
  display,
  disabled = false,
  menuClassName = "",
  className = "",
  triggerClassName = "",
  hideChevron = false,
  menuWidth = 180,
  menuMaxHeight = 320,
  children,
  title,
}) {
  const open = openId === id;
  const triggerRef = useRef(null);
  const menuStyle = usePortaledMenuStyle(
    open,
    triggerRef,
    menuWidth,
    menuMaxHeight
  );

  return (
    <div
      className={`sp-dd${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`sp-dd__trigger${triggerClassName ? ` ${triggerClassName}` : ""}`}
        disabled={disabled}
        title={title || label}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpenId((p) => (p === id ? null : id))}
      >
        <span className="sp-dd__value">{display}</span>
        {hideChevron ? null : <Chevron />}
      </button>
      {open && menuStyle && typeof document !== "undefined"
        ? createPortal(
            <div
              className={`sp-dd__menu ${menuClassName}`.trim()}
              role="listbox"
              aria-label={label}
              style={menuStyle}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/**
 * Input + preset chevron combobox (Text field).
 */
export function CuteCombo({
  id,
  openId,
  setOpenId,
  label = "Text",
  value = "",
  onChange,
  inputRef,
  disabled = false,
  placeholder = "Type or pick label…",
  menuClassName = "",
  className = "",
  menuWidth = 180,
  children,
}) {
  const open = openId === id;
  const rootRef = useRef(null);
  const menuStyle = usePortaledMenuStyle(open, rootRef, menuWidth);

  return (
    <div
      ref={rootRef}
      className={`sp-dd sp-dd--combo${open ? " is-open" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <div
        className={`sp-combo${disabled ? " is-disabled" : ""}${
          open ? " is-open" : ""
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          className="sp-combo__input"
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          onChange={(e) => onChange?.(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onFocus={() => {
            if (disabled) return;
            setOpenId(id);
          }}
          onClick={() => {
            if (disabled) return;
            setOpenId(id);
          }}
        />
        <button
          type="button"
          className="sp-combo__chev"
          disabled={disabled}
          title="Presets"
          aria-label="Open label presets"
          aria-expanded={open}
          onClick={() => setOpenId((p) => (p === id ? null : id))}
        >
          <Chevron />
        </button>
      </div>
      {open && menuStyle && typeof document !== "undefined"
        ? createPortal(
            <div
              className={`sp-dd__menu ${menuClassName}`.trim()}
              role="listbox"
              aria-label="Label presets"
              style={menuStyle}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default CuteDropdown;
